import {
  LayoutDashboard,
  ChartColumn,
  Users,
  UsersRound,
  Settings,
  ClipboardCheck,
  CircleCheckBig,
  Target,
  Trophy,
  Home,
  Briefcase,
  Activity,
  BookOpen,
} from "lucide-react";

import { SidebarMenuItem } from "../../Components/Sidebar";
import { RelationshipsIcon } from "../../Components/Sidebar/icons";

/** Login role slugs for account-team users (AE, CSM, SE, etc.) */
export const ACCOUNT_TEAM_LOGIN_ROLES = {
  AE: "ae",
  CSM: "csm",
  SE: "se",
  TAM: "tam",
  BCS: "bcs",
  SDR: "sdr",
  VP: "vp",
} as const;

export type AccountTeamLoginRoleType =
  (typeof ACCOUNT_TEAM_LOGIN_ROLES)[keyof typeof ACCOUNT_TEAM_LOGIN_ROLES];

export const ACCOUNT_TEAM_LOGIN_ROLE_LIST: readonly AccountTeamLoginRoleType[] =
  Object.values(ACCOUNT_TEAM_LOGIN_ROLES);

/** API/list filter value — sent as `role` query param for account-team user list */
export const ACCOUNT_TEAM_ROLE_FILTER = "account_team";

/** Role names for the executive/sponsor picker */
export const EXECUTIVE_PICKER_ROLES = [
  "executive_sponsor",
  "c_level",
  "executive",
] as const;

export const ROLES = {
  IZBA_ADMIN: "izba_admin",
  VENDOR_ADMIN: "vendor_admin",
  C_LEVEL: "c_level",
  EXECUTIVE: "executive",
  EXECUTIVE_SPONSOR: "executive_sponsor",
  EXECUTIVE_SPONSOR_LEAD: "executive_sponsor_lead",
  ...ACCOUNT_TEAM_LOGIN_ROLES,
} as const;

/** Easy-onboarding invite company_identity_hint values */
export const ONBOARDING_IDENTITY = {
  ACCOUNT_TEAM: "ACCOUNT_TEAM",
  C_LEVEL: "C_LEVEL",
  EXECUTIVE: "executive",
} as const;

export type OnboardingIdentity =
  (typeof ONBOARDING_IDENTITY)[keyof typeof ONBOARDING_IDENTITY];

export const normalizeOnboardingIdentityHint = (hint?: string | null): string =>
  (hint || "").trim();

export const isOnboardingAccountTeamHint = (hint?: string | null): boolean =>
  normalizeOnboardingIdentityHint(hint).toUpperCase() ===
  ONBOARDING_IDENTITY.ACCOUNT_TEAM;

export const isOnboardingCLevelHint = (hint?: string | null): boolean =>
  normalizeOnboardingIdentityHint(hint).toUpperCase() ===
  ONBOARDING_IDENTITY.C_LEVEL;

export const isOnboardingExecutiveHint = (hint?: string | null): boolean =>
  normalizeOnboardingIdentityHint(hint).toLowerCase() ===
  ONBOARDING_IDENTITY.EXECUTIVE;

/** C-Level and Executive share the same two-step onboarding wizard */
export const isOnboardingLeadershipHint = (hint?: string | null): boolean =>
  isOnboardingCLevelHint(hint) || isOnboardingExecutiveHint(hint);

/** All onboarding invite roles use the shared grade dropdown */
export const ONBOARDING_GRADE_IDENTITIES: readonly OnboardingIdentity[] = [
  ONBOARDING_IDENTITY.ACCOUNT_TEAM,
  ONBOARDING_IDENTITY.C_LEVEL,
  ONBOARDING_IDENTITY.EXECUTIVE,
];

export type RoleType = (typeof ROLES)[keyof typeof ROLES];

/** Account-team member role labels on customer records (API / forms) */
export const ACCOUNT_TEAM_ROLES = {
  AE: "AE",
  CSM: "CSM",
  SE: "SE",
  TAM: "TAM",
  BCS: "BCS",
  SDR: "SDR",
  VP: "VP",
} as const;

export type AccountTeamRoleType =
  (typeof ACCOUNT_TEAM_ROLES)[keyof typeof ACCOUNT_TEAM_ROLES];

export const ACCOUNT_TEAM_ALLOWED_ROLES: readonly AccountTeamRoleType[] =
  Object.values(ACCOUNT_TEAM_ROLES);

/** Role names for the account team member picker (uppercase to match DB) */
export const ACCOUNT_TEAM_PICKER_ROLES: string[] = [
  ...ACCOUNT_TEAM_ALLOWED_ROLES,
];

/** Human-readable labels for account-team role codes (AE, CSM, etc.) */
export const ACCOUNT_TEAM_ROLE_DISPLAY_LABELS: Record<
  AccountTeamRoleType,
  string
> = {
  AE: "Account Executive",
  SE: "Solution Architect",
  VP: "Vice President",
  BCS: "Business Continuity Services",
  CSM: "Customer Success Manager",
  SDR: "Sales Development Representative",
  TAM: "Technical Account Manager",
};

export const getAccountTeamRoleDisplayLabel = (role: string): string => {
  const code = role.trim().toUpperCase() as AccountTeamRoleType;
  return ACCOUNT_TEAM_ROLE_DISPLAY_LABELS[code] ?? role;
};

export const ACCOUNT_TEAM_ROLE_OPTIONS = ACCOUNT_TEAM_ALLOWED_ROLES.map(
  role => ({
    label: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS[role],
    value: role,
  })
);

/** Customer-team MEDDPICC stakeholder roles (stored in customer_team_members.role) */
export const CUSTOMER_TEAM_ROLES = {
  NA: "Not Applicable",
  EX: "Executive",
  EB: "Economic Buyer",
  DM: "Decision Maker",
  CH: "Champion",
  IN: "Influencer",
  TB: "Technical Buyer",
  PR: "Procurement",
  IT: "IT Stakeholder",
  EU: "End User",
} as const;

export type CustomerTeamRoleType =
  (typeof CUSTOMER_TEAM_ROLES)[keyof typeof CUSTOMER_TEAM_ROLES];

export const CUSTOMER_TEAM_ALLOWED_ROLES: readonly CustomerTeamRoleType[] =
  Object.values(CUSTOMER_TEAM_ROLES);

export const CUSTOMER_TEAM_ROLE_OPTIONS = CUSTOMER_TEAM_ALLOWED_ROLES.map(
  role => ({
    label: role,
    value: role,
  })
);

export const CUSTOMER_TEAM_ROLE_VALIDATION_MESSAGE = `Role must be one of: ${CUSTOMER_TEAM_ALLOWED_ROLES.join(", ")}`;

/** Case-insensitive match to canonical MEDDPICC role label, or null if invalid. */
export const normalizeCustomerTeamRole = (
  role: string | null | undefined
): CustomerTeamRoleType | null => {
  const trimmed = String(role ?? "").trim();
  if (!trimmed) return null;
  return (
    CUSTOMER_TEAM_ALLOWED_ROLES.find(
      allowed => allowed.toLowerCase() === trimmed.toLowerCase()
    ) ?? null
  );
};

export const ROLE_DISPLAY_NAMES: Record<RoleType, string> = {
  [ROLES.IZBA_ADMIN]: "Izba Admin",
  [ROLES.VENDOR_ADMIN]: "Vendor Administrator",
  [ROLES.C_LEVEL]: "C Level",
  [ROLES.EXECUTIVE]: "Executive",
  [ROLES.EXECUTIVE_SPONSOR]: "Executive Sponsor",
  [ROLES.EXECUTIVE_SPONSOR_LEAD]: "Executive Sponsor Lead",
  [ROLES.AE]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.AE,
  [ROLES.CSM]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.CSM,
  [ROLES.SE]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.SE,
  [ROLES.TAM]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.TAM,
  [ROLES.BCS]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.BCS,
  [ROLES.SDR]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.SDR,
  [ROLES.VP]: ACCOUNT_TEAM_ROLE_DISPLAY_LABELS.VP,
};

/**
 * Approver dropdown groupings for assignment review selects.
 * Roles must exist on `ROLES`; unknown API roles are left ungrouped.
 */
export const APPROVER_SELECT_ROLE_GROUPS: ReadonlyArray<{
  label: string;
  roles: readonly RoleType[];
}> = [
  {
    label: "Executive Leaders",
    roles: [ROLES.EXECUTIVE_SPONSOR_LEAD, ROLES.C_LEVEL],
  },
  {
    label: "Administrators",
    roles: [ROLES.VENDOR_ADMIN, ROLES.IZBA_ADMIN],
  },
  {
    label: "Executive Sponsors",
    roles: [ROLES.EXECUTIVE_SPONSOR, ROLES.EXECUTIVE],
  },
];

export const ROLE_DEFAULT_ROUTES: Record<RoleType, string> = {
  [ROLES.IZBA_ADMIN]: "/izba-admin",
  [ROLES.VENDOR_ADMIN]: "/home",
  [ROLES.C_LEVEL]: "/home",
  [ROLES.EXECUTIVE]: "/home",
  [ROLES.EXECUTIVE_SPONSOR]: "/home",
  [ROLES.EXECUTIVE_SPONSOR_LEAD]: "/home",
  [ROLES.AE]: "/home",
  [ROLES.CSM]: "/home",
  [ROLES.SE]: "/home",
  [ROLES.TAM]: "/home",
  [ROLES.BCS]: "/home",
  [ROLES.SDR]: "/home",
  [ROLES.VP]: "/home",
};

export const RELATIONSHIP_ROUTE_PATH = "/relationship";
export const TEAM_VIEWS_ROUTE_PATH = "/team-views";
export const COVERAGE_ROUTE_PATH = "/coverage";
/** Coverage lives on Executive Operations, same as the other tabs on that page. */
export const COVERAGE_OPS_PATH = "/ops?tab=coverage";

/** Menu paths gated by org RL-assessments setting. */
const RL_ASSESSMENTS_GATED_PATHS = new Set([RELATIONSHIP_ROUTE_PATH]);

export const filterMenuItemsForRelationshipAccess = (
  items: SidebarMenuItem[],
  settingsResolved: boolean,
  rlAssessmentsEnabled: boolean
): SidebarMenuItem[] => {
  if (!settingsResolved || !rlAssessmentsEnabled) {
    return items.filter(item => !RL_ASSESSMENTS_GATED_PATHS.has(item.path));
  }
  return items;
};

/**
 * Shared sidebar navigation for all account-team login roles
 */
/** Shared sidebar navigation for all account-team login roles */
export const GUIDANCE_LIBRARY_MENU_ITEM: SidebarMenuItem = {
  name: "Guidance Library",
  path: "/guidance-library",
  lucideIcon: BookOpen,
};

export const ACCOUNT_TEAM_MENU_ITEMS: SidebarMenuItem[] = [
  {
    name: "Home",
    path: "/home",
    lucideIcon: Home,
  },
  GUIDANCE_LIBRARY_MENU_ITEM,
  {
    name: "My Accounts",
    path: "/my-portfolio-accounts",
    lucideIcon: Briefcase,
  },
  {
    name: "Executive Staff",
    path: "/executive-staff",
    lucideIcon: Users,
  },
  {
    name: "Team Views",
    path: "/team-views",
    lucideIcon: UsersRound,
  },
  {
    name: "Always On Intelligence",
    path: "/always-on-channel",
    lucideIcon: Activity,
  },
  {
    name: "Relationships",
    path: "/relationship",
    lucideIcon: RelationshipsIcon,
  },
  {
    name: "My Assessments",
    path: "/pending-assessments",
    lucideIcon: ClipboardCheck,
  },
  {
    name: "My Check-Ins",
    path: "/team-sponsor-feedback",
    lucideIcon: CircleCheckBig,
  },
  {
    name: "Goals & Milestones",
    path: "/goals-milestones",
    lucideIcon: Target,
  },
  {
    name: "Leaderboard",
    path: "/leaderboard",
    lucideIcon: Trophy,
  },
  {
    name: "Dashboard",
    path: "/dashboard",
    lucideIcon: LayoutDashboard,
  },
];

/**
 * Menu items configuration for each role
 */
export const ROLE_MENU_ITEMS: Record<RoleType, SidebarMenuItem[]> = {
  [ROLES.IZBA_ADMIN]: [
    {
      name: "Home",
      lucideIcon: Home,
      path: "/izba-admin",
    },
  ],
  [ROLES.VENDOR_ADMIN]: [
    {
      name: "Home",
      path: "/home",
      lucideIcon: Home,
    },
    GUIDANCE_LIBRARY_MENU_ITEM,
    {
      name: "Exec Ops",
      path: "/ops",
      lucideIcon: ChartColumn,
    },
    {
      name: "Executive Staff",
      path: "/executive-staff",
      lucideIcon: Users,
    },
    {
      name: "Team Views",
      path: "/team-views",
      lucideIcon: UsersRound,
    },
    {
      name: "Always On Intelligence",
      path: "/always-on-channel",
      lucideIcon: Activity,
    },
    {
      name: "Relationships",
      path: "/relationship",
      lucideIcon: RelationshipsIcon,
    },
    {
      name: "Goals & Milestones",
      path: "/goals-milestones",
      lucideIcon: Target,
    },
    {
      name: "Leaderboard",
      path: "/leaderboard",
      lucideIcon: Trophy,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      lucideIcon: LayoutDashboard,
    },
    {
      name: "Settings",
      path: "/settings",
      lucideIcon: Settings,
    },
  ],
  [ROLES.C_LEVEL]: [
    {
      name: "Home",
      path: "/home",
      lucideIcon: Home,
    },
    GUIDANCE_LIBRARY_MENU_ITEM,
    {
      name: "Exec Ops",
      path: "/ops",
      lucideIcon: ChartColumn,
    },
    {
      name: "Executive Staff",
      path: "/executive-staff",
      lucideIcon: Users,
    },
    {
      name: "Team Views",
      path: "/team-views",
      lucideIcon: UsersRound,
    },
    {
      name: "Always On Intelligence",
      path: "/always-on-channel",
      lucideIcon: Activity,
    },
    {
      name: "Relationships",
      path: "/relationship",
      lucideIcon: RelationshipsIcon,
    },
    {
      name: "My Assessments",
      path: "/pending-assessments",
      lucideIcon: ClipboardCheck,
    },
    {
      name: "Goals & Milestones",
      path: "/goals-milestones",
      lucideIcon: Target,
    },
    {
      name: "Leaderboard",
      path: "/leaderboard",
      lucideIcon: Trophy,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      lucideIcon: LayoutDashboard,
    },
  ],
  [ROLES.EXECUTIVE]: [
    {
      name: "Home",
      path: "/home",
      lucideIcon: Home,
    },
    GUIDANCE_LIBRARY_MENU_ITEM,
    {
      name: "My Accounts",
      path: "/my-portfolio-accounts",
      lucideIcon: Briefcase,
    },
    {
      name: "Executive Staff",
      path: "/executive-staff",
      lucideIcon: Users,
    },
    {
      name: "Team Views",
      path: "/team-views",
      lucideIcon: UsersRound,
    },
    {
      name: "Always On Intelligence",
      path: "/always-on-channel",
      lucideIcon: Activity,
    },
    {
      name: "Relationships",
      path: "/relationship",
      lucideIcon: RelationshipsIcon,
    },
    {
      name: "My Assessments",
      path: "/pending-assessments",
      lucideIcon: ClipboardCheck,
    },
    {
      name: "Goals & Milestones",
      path: "/goals-milestones",
      lucideIcon: Target,
    },
    {
      name: "leaderboard",
      path: "/leaderboard",
      lucideIcon: Trophy,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      lucideIcon: LayoutDashboard,
    },
  ],
  [ROLES.EXECUTIVE_SPONSOR]: [
    {
      name: "Home",
      path: "/home",
      lucideIcon: Home,
    },
    GUIDANCE_LIBRARY_MENU_ITEM,
    {
      name: "My Accounts",
      path: "/my-portfolio-accounts",
      lucideIcon: Briefcase,
    },
    {
      name: "Executive Staff",
      path: "/executive-staff",
      lucideIcon: Users,
    },
    {
      name: "Team Views",
      path: "/team-views",
      lucideIcon: UsersRound,
    },
    {
      name: "Always On Intelligence",
      path: "/always-on-channel",
      lucideIcon: Activity,
    },
    {
      name: "Relationships",
      path: "/relationship",
      lucideIcon: RelationshipsIcon,
    },
    {
      name: "My Assessments",
      path: "/pending-assessments",
      lucideIcon: ClipboardCheck,
    },
    {
      name: "My Check-Ins",
      path: "/my-check-ins",
      lucideIcon: CircleCheckBig,
    },
    {
      name: "Goals & Milestones",
      path: "/goals-milestones",
      lucideIcon: Target,
    },
    {
      name: "Leaderboard",
      path: "/leaderboard",
      lucideIcon: Trophy,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      lucideIcon: LayoutDashboard,
    },
  ],
  [ROLES.EXECUTIVE_SPONSOR_LEAD]: [
    {
      name: "Home",
      path: "/home",
      lucideIcon: Home,
    },
    GUIDANCE_LIBRARY_MENU_ITEM,
    {
      name: "Exec Ops",
      path: "/ops",
      lucideIcon: ChartColumn,
    },
    {
      name: "Executive Staff",
      path: "/executive-staff",
      lucideIcon: Users,
    },
    {
      name: "Team Views",
      path: "/team-views",
      lucideIcon: UsersRound,
    },
    {
      name: "Always On Intelligence",
      path: "/always-on-channel",
      lucideIcon: Activity,
    },
    {
      name: "Relationships",
      path: "/relationship",
      lucideIcon: RelationshipsIcon,
    },
    {
      name: "Goals & Milestones",
      path: "/goals-milestones",
      lucideIcon: Target,
    },
    {
      name: "Leaderboard",
      path: "/leaderboard",
      lucideIcon: Trophy,
    },
    {
      name: "Dashboard",
      path: "/dashboard",
      lucideIcon: LayoutDashboard,
    },
    {
      name: "Settings",
      path: "/settings",
      lucideIcon: Settings,
    },
  ],
  [ROLES.AE]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.CSM]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.SE]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.TAM]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.BCS]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.SDR]: ACCOUNT_TEAM_MENU_ITEMS,
  [ROLES.VP]: ACCOUNT_TEAM_MENU_ITEMS,
};

/**
 * Activity page paths for each role (used to determine background styling)
 */
export const ROLE_ACTIVITY_PATHS: Record<RoleType, string[]> = {
  [ROLES.IZBA_ADMIN]: ["/izba-admin"],
  [ROLES.VENDOR_ADMIN]: ["/ops", "/"],
  [ROLES.C_LEVEL]: ["/ops", "/"],
  [ROLES.EXECUTIVE]: ["/"],
  [ROLES.EXECUTIVE_SPONSOR]: ["/ops", "/"],
  [ROLES.EXECUTIVE_SPONSOR_LEAD]: ["/ops", "/"],
  [ROLES.AE]: ["/"],
  [ROLES.CSM]: ["/"],
  [ROLES.SE]: ["/"],
  [ROLES.TAM]: ["/"],
  [ROLES.BCS]: ["/"],
  [ROLES.SDR]: ["/"],
  [ROLES.VP]: ["/"],
};

/**
 * Gets menu items for a role
 */
export const getMenuItemsForRole = (
  userRole: string | { name?: string } | null | undefined
): SidebarMenuItem[] => {
  const normalizedRole = normalizeRoleName(userRole);

  if (!normalizedRole) return [];

  const roleValues = Object.values(ROLES);
  if (!roleValues.includes(normalizedRole as RoleType)) return [];

  return ROLE_MENU_ITEMS[normalizedRole as RoleType] || [];
};

/**
 * Normalizes a role name by converting to lowercase and trimming whitespace
 */
export const normalizeRoleName = (
  role: string | { name?: string } | null | undefined
): string | null => {
  if (!role) return null;
  const roleName = typeof role === "string" ? role : role?.name;
  return roleName?.toLowerCase().trim() || null;
};

export const isAccountTeamRole = (
  userRole: string | { name?: string } | null | undefined
): boolean => {
  const normalizedRole = normalizeRoleName(userRole);
  return (
    !!normalizedRole &&
    ACCOUNT_TEAM_LOGIN_ROLE_LIST.includes(
      normalizedRole as AccountTeamLoginRoleType
    )
  );
};

/** Role key for admin video ordering — one bucket per concrete role. */
export const getVideoRoleGroupKey = (role?: string | null): string =>
  role || "";

/**
 * Checks if a role matches a specific role constant
 */
export const isRole = (
  userRole: string | { name?: string } | null | undefined,
  targetRole: RoleType
): boolean => {
  const normalizedRole = normalizeRoleName(userRole);
  return normalizedRole === targetRole;
};

/**
 * Gets the default route for a role
 */
export const getDefaultRouteForRole = (
  userRole: string | { name?: string } | null | undefined
): string => {
  const normalizedRole = normalizeRoleName(userRole);
  const roleValues = Object.values(ROLES);

  if (normalizedRole && roleValues.includes(normalizedRole as RoleType)) {
    return ROLE_DEFAULT_ROUTES[normalizedRole as RoleType];
  }

  return "/home";
};
